import { AzureBlobStorageService } from './AzureBlobStorageService';

/**
 * Encrypted Image Storage Service for Fitzaa
 * 
 * This service handles:
 * - Image optimization and compression
 * - Client-side encryption before upload
 * - Secure storage via Firebase Functions to Azure Blob Storage
 * - Image retrieval with decryption
 */
export class EncryptedImageStorageService {
    private static instance: EncryptedImageStorageService;
    private azureBlobService: AzureBlobStorageService | null = null;
    private encryptionKey: CryptoKey | null = null;
    private isInitialized: boolean = false;

    // Image optimization settings - optimized for under 400KB
    private readonly MAX_WIDTH = 1200;
    private readonly MAX_HEIGHT = 1200;
    private readonly QUALITY = 0.7;
    private readonly MAX_FILE_SIZE = 400 * 1024; // 400KB in bytes

    // Thumbnail settings
    private readonly THUMBNAIL_WIDTH = 300;
    private readonly THUMBNAIL_HEIGHT = 300;
    private readonly THUMBNAIL_QUALITY = 0.6;
    private readonly THUMBNAIL_MAX_SIZE = 50 * 1024; // 50KB in bytes

    private constructor() { }

    /**
     * Get singleton instance
     */
    static getInstance(): EncryptedImageStorageService {
        if (!EncryptedImageStorageService.instance) {
            EncryptedImageStorageService.instance = new EncryptedImageStorageService();
        }
        return EncryptedImageStorageService.instance;
    }

    /**
     * Initialize the service
     */
    async initialize(): Promise<void> {
        if (this.isInitialized) {
            return;
        }

        try {
            // Get or create Azure Blob Storage service instance
            this.azureBlobService = AzureBlobStorageService.getInstance();

            // Generate or derive encryption key
            await this.generateEncryptionKey();

            this.isInitialized = true;
            console.log('[EncryptedImageStorageService] Service initialized successfully');
        } catch (error) {
            console.error('[EncryptedImageStorageService] Initialization failed:', error);
            throw error;
        }
    }

    /**
     * Generate encryption key for image encryption/decryption
     */
    private async generateEncryptionKey(): Promise<void> {
        try {
            // Generate a random encryption key
            this.encryptionKey = await window.crypto.subtle.generateKey(
                {
                    name: 'AES-CBC',
                    length: 256
                },
                true, // extractable
                ['encrypt', 'decrypt']
            );

            console.log('[EncryptedImageStorageService] Encryption key generated successfully');
        } catch (error) {
            console.error('[EncryptedImageStorageService] Failed to generate encryption key:', error);
            throw error;
        }
    }

    /**
     * Export the current encryption key as a string for storage
     */
    private async exportEncryptionKey(): Promise<string> {
        if (!this.encryptionKey) {
            throw new Error('No encryption key available for export');
        }

        try {
            const exportedKey = await window.crypto.subtle.exportKey('raw', this.encryptionKey);
            const uint8Array = new Uint8Array(exportedKey);
            return btoa(Array.from(uint8Array, byte => String.fromCharCode(byte)).join(''));
        } catch (error) {
            console.error('[EncryptedImageStorageService] Failed to export encryption key:', error);
            throw new Error('Failed to export encryption key');
        }
    }

    /**
     * Convert Blob to base64 string for Firebase Functions
     */
    private async blobToBase64(blob: Blob): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const result = reader.result as string;
                // Remove the data URL prefix (e.g., "data:application/octet-stream;base64,")
                const base64 = result.split(',')[1];
                resolve(base64);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    }

    /**
     * Optimize and compress image for storage - ensures file is under 400KB
     */
    private async optimizeImage(imageFile: File): Promise<{ optimizedBlob: Blob; originalSize: number; optimizedSize: number }> {
        return new Promise((resolve, reject) => {
            try {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                const img = new Image();

                img.onload = () => {
                    try {
                        // Calculate optimal dimensions while maintaining aspect ratio
                        const { width, height } = this.calculateOptimalDimensions(img.width, img.height);

                        // Set canvas dimensions
                        canvas.width = width;
                        canvas.height = height;

                        // Draw and resize image
                        ctx?.drawImage(img, 0, 0, width, height);

                        // Try different quality levels to get under 400KB
                        this.compressToTargetSize(canvas, imageFile.size, 0, (blob) => {
                            if (blob) {
                                const originalSize = imageFile.size;
                                const optimizedSize = blob.size;

                                console.log('[EncryptedImageStorageService] Image optimization complete:', {
                                    original: `${(originalSize / 1024).toFixed(1)}KB`,
                                    optimized: `${(optimizedSize / 1024).toFixed(1)}KB`,
                                    compression: `${((1 - optimizedSize / originalSize) * 100).toFixed(1)}%`,
                                    dimensions: `${width}x${height}px`,
                                    targetMet: optimizedSize <= this.MAX_FILE_SIZE ? 'YES' : 'NO'
                                });

                                resolve({
                                    optimizedBlob: blob,
                                    originalSize,
                                    optimizedSize
                                });
                            } else {
                                reject(new Error('Failed to create optimized image blob'));
                            }
                        });
                    } catch (error) {
                        reject(error);
                    }
                };

                img.onerror = () => reject(new Error('Failed to load image'));
                img.src = URL.createObjectURL(imageFile);
            } catch (error) {
                reject(error);
            }
        });
    }

    /**
     * Recursively compress image to target size (400KB)
     */
    private compressToTargetSize(canvas: HTMLCanvasElement, originalSize: number, attempt: number, callback: (blob: Blob | null) => void): void {
        // Quality levels to try (decreasing)
        const qualityLevels = [0.7, 0.6, 0.5, 0.4, 0.3, 0.25, 0.2, 0.15, 0.1];
        const quality = attempt < qualityLevels.length ? qualityLevels[attempt] : 0.1;

        canvas.toBlob((blob) => {
            if (!blob) {
                callback(null);
                return;
            }

            // If we're under the target size or this is our last attempt, use this blob
            if (blob.size <= this.MAX_FILE_SIZE || attempt >= qualityLevels.length - 1) {
                callback(blob);
                return;
            }

            // If still too large, try with lower quality
            this.compressToTargetSize(canvas, originalSize, attempt + 1, callback);
        }, 'image/jpeg', quality);
    }

    /**
     * Generate thumbnail for web display - optimized for 50KB
     */
    private async generateThumbnail(imageFile: File): Promise<Blob> {
        return new Promise((resolve, reject) => {
            try {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                const img = new Image();

                img.onload = () => {
                    try {
                        // Try different dimensions if needed to achieve target size
                        const dimensionsToTry = [
                            { width: this.THUMBNAIL_WIDTH, height: this.THUMBNAIL_HEIGHT },
                            { width: 200, height: 200 },
                            { width: 150, height: 150 },
                            { width: 100, height: 100 }
                        ];

                        this.tryThumbnailDimensions(canvas, ctx, img, dimensionsToTry, 0, (blob) => {
                            if (blob) {
                                const sizeKB = blob.size / 1024;
                                const targetKB = this.THUMBNAIL_MAX_SIZE / 1024;
                                console.log(`[EncryptedImageStorageService] Thumbnail generated: ${sizeKB.toFixed(1)}KB (target: ${targetKB.toFixed(1)}KB)`);
                                resolve(blob);
                            } else {
                                reject(new Error('Failed to create thumbnail blob'));
                            }
                        });
                    } catch (error) {
                        reject(error);
                    }
                };

                img.onerror = () => reject(new Error('Failed to load image for thumbnail'));
                img.src = URL.createObjectURL(imageFile);
            } catch (error) {
                reject(error);
            }
        });
    }

    /**
     * Try different thumbnail dimensions to achieve target size
     */
    private tryThumbnailDimensions(
        canvas: HTMLCanvasElement,
        ctx: CanvasRenderingContext2D | null,
        img: HTMLImageElement,
        dimensions: Array<{ width: number, height: number }>,
        dimensionIndex: number,
        callback: (blob: Blob | null) => void
    ): void {
        if (dimensionIndex >= dimensions.length) {
            callback(null);
            return;
        }

        const dims = dimensions[dimensionIndex];
        canvas.width = dims.width;
        canvas.height = dims.height;

        // Draw and resize image to current dimensions
        ctx?.drawImage(img, 0, 0, dims.width, dims.height);

        // Compress thumbnail to target size (50KB)
        this.compressThumbnailToTargetSize(canvas, 0, (blob) => {
            if (blob && blob.size <= this.THUMBNAIL_MAX_SIZE) {
                // Success! We achieved the target size
                callback(blob);
            } else if (dimensionIndex < dimensions.length - 1) {
                // Try next smaller dimension
                console.log(`[EncryptedImageStorageService] Trying smaller dimensions: ${dims.width}x${dims.height} -> ${dimensions[dimensionIndex + 1].width}x${dimensions[dimensionIndex + 1].height}`);
                this.tryThumbnailDimensions(canvas, ctx, img, dimensions, dimensionIndex + 1, callback);
            } else {
                // Use the last attempt even if it's over the target size
                callback(blob);
            }
        });
    }

    /**
     * Recursively compress thumbnail to target size (50KB)
     */
    private compressThumbnailToTargetSize(canvas: HTMLCanvasElement, attempt: number, callback: (blob: Blob | null) => void): void {
        // More aggressive quality levels to ensure we get under 50KB
        const qualityLevels = [0.5, 0.4, 0.3, 0.25, 0.2, 0.15, 0.1, 0.08, 0.05, 0.03, 0.02, 0.01];
        const quality = attempt < qualityLevels.length ? qualityLevels[attempt] : 0.01;

        canvas.toBlob((blob) => {
            if (!blob) {
                callback(null);
                return;
            }

            const sizeKB = blob.size / 1024;
            const targetKB = this.THUMBNAIL_MAX_SIZE / 1024;
            console.log(`[EncryptedImageStorageService] Thumbnail compression attempt ${attempt + 1}: ${sizeKB.toFixed(1)}KB (target: ${targetKB.toFixed(1)}KB) quality: ${(quality * 100).toFixed(1)}%`);

            // If we're under the target size or this is our last attempt, use this blob
            if (blob.size <= this.THUMBNAIL_MAX_SIZE || attempt >= qualityLevels.length - 1) {
                if (blob.size <= this.THUMBNAIL_MAX_SIZE) {
                    console.log(`[EncryptedImageStorageService] ✅ Thumbnail target size achieved with quality ${(quality * 100).toFixed(1)}%`);
                } else {
                    console.log(`[EncryptedImageStorageService] ⚠️ Thumbnail final size: ${sizeKB.toFixed(1)}KB (target: ${targetKB.toFixed(1)}KB)`);
                }
                callback(blob);
                return;
            }

            // If still too large, try with lower quality
            this.compressThumbnailToTargetSize(canvas, attempt + 1, callback);
        }, 'image/jpeg', quality);
    }

    /**
     * Calculate optimal dimensions while maintaining aspect ratio
     */
    private calculateOptimalDimensions(originalWidth: number, originalHeight: number): { width: number; height: number } {
        let { width, height } = { width: originalWidth, height: originalHeight };

        // Scale down if larger than max dimensions
        if (width > this.MAX_WIDTH || height > this.MAX_HEIGHT) {
            const aspectRatio = width / height;

            if (width > height) {
                width = this.MAX_WIDTH;
                height = width / aspectRatio;
            } else {
                height = this.MAX_HEIGHT;
                width = height * aspectRatio;
            }
        }

        return { width: Math.round(width), height: Math.round(height) };
    }

    /**
     * Encrypt image data using AES-GCM
     */
    private async encryptImage(imageData: ArrayBuffer): Promise<{ encryptedData: ArrayBuffer; iv: Uint8Array }> {
        if (!this.encryptionKey) {
            throw new Error('Encryption key not available');
        }

        try {
            // Generate random IV (16 bytes for AES-CBC)
            const iv = window.crypto.getRandomValues(new Uint8Array(16));

            // Encrypt the image data using CBC mode for React Native compatibility
            const encryptedData = await window.crypto.subtle.encrypt(
                {
                    name: 'AES-CBC',
                    iv: iv
                },
                this.encryptionKey,
                imageData
            );

            return { encryptedData, iv };
        } catch (error) {
            console.error('[EncryptedImageStorageService] Encryption failed:', error);
            throw new Error('Failed to encrypt image data');
        }
    }

    /**
     * Decrypt image data using AES-CBC
     */
    async decryptImage(encryptedData: ArrayBuffer, iv: Uint8Array, encryptionKeyString?: string): Promise<ArrayBuffer> {
        let keyToUse = this.encryptionKey;

        // If a specific encryption key is provided, use it instead of the default key
        if (encryptionKeyString) {
            try {
                keyToUse = await this.importEncryptionKey(encryptionKeyString);
            } catch (error) {
                console.error('[EncryptedImageStorageService] Failed to import encryption key:', error);
                throw new Error('Failed to import encryption key for decryption');
            }
        }

        if (!keyToUse) {
            throw new Error('No encryption key available for decryption');
        }

        try {
            const decryptedData = await window.crypto.subtle.decrypt(
                {
                    name: 'AES-CBC',
                    iv: new Uint8Array(iv)
                },
                keyToUse,
                encryptedData
            );

            return decryptedData;
        } catch (error) {
            console.error('[EncryptedImageStorageService] Decryption failed:', error);
            throw new Error('Failed to decrypt image data');
        }
    }

    /**
     * Import encryption key from a base64 string
     * Handles both web-generated keys (Uint8Array format) and mobile-generated keys (CryptoJS format)
     */
    private async importEncryptionKey(encryptionKeyString: string): Promise<CryptoKey> {
        try {
            console.log('[EncryptedImageStorageService] Importing encryption key, length:', encryptionKeyString.length);

            // Try the standard web format first (Uint8Array format)
            try {
                const keyBytes = Uint8Array.from(atob(encryptionKeyString), c => c.charCodeAt(0));

                const importedKey = await window.crypto.subtle.importKey(
                    'raw',
                    keyBytes,
                    {
                        name: 'AES-CBC',
                        length: 256
                    },
                    false, // not extractable
                    ['decrypt']
                );

                console.log('[EncryptedImageStorageService] Successfully imported key using web format');
                return importedKey;
            } catch (webFormatError) {
                console.log('[EncryptedImageStorageService] Web format failed, trying mobile format:', webFormatError);

                // If that fails, try the mobile format (CryptoJS format)
                // Mobile uses CryptoJS.enc.Base64.stringify() which might have different padding
                try {
                    // Clean the string and ensure proper base64 format
                    let cleanKey = encryptionKeyString.replace(/\s/g, '');

                    // Add padding if needed
                    while (cleanKey.length % 4 !== 0) {
                        cleanKey += '=';
                    }

                    const keyBytes = Uint8Array.from(atob(cleanKey), c => c.charCodeAt(0));

                    const importedKey = await window.crypto.subtle.importKey(
                        'raw',
                        keyBytes,
                        {
                            name: 'AES-CBC',
                            length: 256
                        },
                        false, // not extractable
                        ['decrypt']
                    );

                    console.log('[EncryptedImageStorageService] Successfully imported key using mobile format');
                    return importedKey;
                } catch (mobileFormatError) {
                    const webErrorMsg = webFormatError instanceof Error ? webFormatError.message : String(webFormatError);
                    const mobileErrorMsg = mobileFormatError instanceof Error ? mobileFormatError.message : String(mobileFormatError);

                    console.error('[EncryptedImageStorageService] Both formats failed:', {
                        webFormatError: webErrorMsg,
                        mobileFormatError: mobileErrorMsg,
                        keyLength: encryptionKeyString.length,
                        keyPreview: encryptionKeyString.substring(0, 20) + '...'
                    });
                    throw new Error(`Failed to import encryption key in any format: ${mobileErrorMsg}`);
                }
            }
        } catch (error) {
            console.error('[EncryptedImageStorageService] Failed to import encryption key:', error);
            throw new Error('Failed to import encryption key');
        }
    }

    /**
     * Upload encrypted and optimized image to Azure Blob Storage via Firebase Functions
     */
    async uploadEncryptedImage(
        imageFile: File,
        challengeId: string,
        userId: string,
        metadata: Record<string, string> = {}
    ): Promise<{
        blobName: string;
        thumbnailBlobName: string;
        encryptedUrl: string;
        thumbnailUrl: string;
        metadata: Record<string, string>;
        encryptionKey: string; // Export the key for storage
        optimizationStats: {
            originalSize: number;
            optimizedSize: number;
            compressionRatio: number;
            dimensions: string;
        };
    }> {
        if (!this.isInitialized) {
            await this.initialize();
        }

        try {
            console.log('[EncryptedImageStorageService] Starting encrypted image upload...');

            // Generate both full image and thumbnail
            const [{ optimizedBlob, originalSize, optimizedSize }, thumbnailBlob] = await Promise.all([
                this.optimizeImage(imageFile),
                this.generateThumbnail(imageFile)
            ]);

            // Convert both images to ArrayBuffer
            const [imageArrayBuffer, thumbnailArrayBuffer] = await Promise.all([
                optimizedBlob.arrayBuffer(),
                thumbnailBlob.arrayBuffer()
            ]);

            // Encrypt both images
            const [{ encryptedData: imageEncryptedData, iv: imageIv }, { encryptedData: thumbnailEncryptedData, iv: thumbnailIv }] = await Promise.all([
                this.encryptImage(imageArrayBuffer),
                this.encryptImage(thumbnailArrayBuffer)
            ]);

            // Create encrypted blobs with IV prepended
            const encryptedImageBlob = new Blob([new Uint8Array(imageIv), imageEncryptedData], { type: 'application/octet-stream' });
            const encryptedThumbnailBlob = new Blob([new Uint8Array(thumbnailIv), thumbnailEncryptedData], { type: 'application/octet-stream' });

            // Log file sizes for debugging
            const originalSizeKB = originalSize / 1024;
            const optimizedSizeKB = optimizedSize / 1024;
            const thumbnailSizeKB = thumbnailBlob.size / 1024;
            const encryptedImageSizeKB = encryptedImageBlob.size / 1024;
            const encryptedThumbnailSizeKB = encryptedThumbnailBlob.size / 1024;

            console.log(`[EncryptedImageStorageService] 📊 File Size Analysis:`);
            console.log(`  Original Image: ${originalSizeKB.toFixed(1)}KB`);
            console.log(`  Optimized Image: ${optimizedSizeKB.toFixed(1)}KB`);
            console.log(`  Thumbnail: ${thumbnailSizeKB.toFixed(1)}KB (target: ${(this.THUMBNAIL_MAX_SIZE / 1024).toFixed(1)}KB)`);
            console.log(`  Encrypted Image: ${encryptedImageSizeKB.toFixed(1)}KB`);
            console.log(`  Encrypted Thumbnail: ${encryptedThumbnailSizeKB.toFixed(1)}KB`);

            // Calculate compression ratio
            const compressionRatio = ((originalSize - optimizedSize) / originalSize) * 100;

            // Export the encryption key as a string for storage
            const exportedKey = await this.exportEncryptionKey();

            // Prepare metadata including encryption info
            const enhancedMetadata = {
                ...metadata,
                challengeId,
                userId,
                uploadDate: new Date().toISOString(),
                encrypted: 'true',
                encryptionAlgorithm: 'AES-GCM',
                encryptionKey: exportedKey, // Store the key with the image
                ivLength: imageIv.length.toString(),
                originalContentType: imageFile.type,
                originalSize: originalSize.toString(),
                optimizedSize: optimizedSize.toString(),
                compressionRatio: compressionRatio.toFixed(1),
                dimensions: `${optimizedBlob.size > 0 ? 'optimized' : 'unknown'}`
            };

            // Generate blob names with industry-standard naming
            const timestamp = Date.now();
            const baseName = `${challengeId}_${userId}_${timestamp}`;
            const imageBlobName = `${baseName}.encrypted`;
            const thumbnailBlobName = `${baseName}_thumb.encrypted`;

            // Upload both encrypted images to Azure Blob Storage via Firebase Functions
            const [imageUploadResult, thumbnailUploadResult] = await Promise.all([
                this.azureBlobService!.uploadPhoto(
                    await this.blobToBase64(encryptedImageBlob),
                    imageBlobName,
                    'application/octet-stream'
                ),
                this.azureBlobService!.uploadPhoto(
                    await this.blobToBase64(encryptedThumbnailBlob),
                    thumbnailBlobName,
                    'application/octet-stream'
                )
            ]);

            console.log('[EncryptedImageStorageService] Encrypted images uploaded successfully:', {
                imageBlobName: imageUploadResult.blobName,
                thumbnailBlobName: thumbnailUploadResult.blobName,
                imageUrl: imageUploadResult.url,
                thumbnailUrl: thumbnailUploadResult.url,
                imageSize: encryptedImageBlob.size,
                thumbnailSize: encryptedThumbnailBlob.size,
                optimization: `${compressionRatio.toFixed(1)}% compression`
            });

            return {
                blobName: imageUploadResult.blobName,
                thumbnailBlobName: thumbnailUploadResult.blobName,
                encryptedUrl: imageUploadResult.url,
                thumbnailUrl: thumbnailUploadResult.url,
                metadata: enhancedMetadata,
                encryptionKey: exportedKey, // Return the key for storage
                optimizationStats: {
                    originalSize,
                    optimizedSize,
                    compressionRatio,
                    dimensions: `${optimizedBlob.size > 0 ? 'optimized' : 'unknown'}`
                }
            };

        } catch (error) {
            console.error('[EncryptedImageStorageService] Encrypted image upload failed:', error);
            throw new Error(`Failed to upload encrypted image: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    /**
     * Upload unencrypted and optimized image to Azure Blob Storage via Firebase Functions
     * This method is more suitable for web display where images need to be rendered directly
     */
    async uploadUnencryptedImage(
        imageFile: File,
        challengeId: string,
        userId: string,
        metadata: Record<string, string> = {}
    ): Promise<{
        blobName: string;
        url: string;
        metadata: Record<string, string>;
        optimizationStats: {
            originalSize: number;
            optimizedSize: number;
            compressionRatio: number;
            dimensions: string;
        };
    }> {
        if (!this.isInitialized) {
            await this.initialize();
        }

        try {
            console.log('[EncryptedImageStorageService] Starting unencrypted image upload...');

            // Optimize the image
            const { optimizedBlob, originalSize, optimizedSize } = await this.optimizeImage(imageFile);

            // Calculate compression ratio
            const compressionRatio = ((originalSize - optimizedSize) / originalSize) * 100;

            // Prepare metadata
            const enhancedMetadata = {
                ...metadata,
                challengeId,
                userId,
                uploadDate: new Date().toISOString(),
                encrypted: 'false',
                originalContentType: imageFile.type,
                originalSize: originalSize.toString(),
                optimizedSize: optimizedSize.toString(),
                compressionRatio: compressionRatio.toFixed(1),
                dimensions: `${optimizedBlob.size > 0 ? 'optimized' : 'unknown'}`
            };

            // Upload optimized image directly to Azure Blob Storage via Firebase Functions
            const uploadResult = await this.azureBlobService!.uploadPhoto(
                await this.blobToBase64(optimizedBlob),
                `${challengeId}_${userId}_${Date.now()}.jpg`,
                imageFile.type
            );

            console.log('[EncryptedImageStorageService] Unencrypted image uploaded successfully:', {
                blobName: uploadResult.blobName,
                url: uploadResult.url,
                size: optimizedBlob.size,
                optimization: `${compressionRatio.toFixed(1)}% compression`
            });

            return {
                blobName: uploadResult.blobName,
                url: uploadResult.url,
                metadata: enhancedMetadata,
                optimizationStats: {
                    originalSize,
                    optimizedSize,
                    compressionRatio,
                    dimensions: `${optimizedBlob.size > 0 ? 'optimized' : 'unknown'}`
                }
            };

        } catch (error) {
            console.error('[EncryptedImageStorageService] Unencrypted image upload failed:', error);
            throw new Error(`Failed to upload unencrypted image: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    /**
     * Get secure access URL for a photo via Firebase Functions
     */
    async getPhotoUrl(blobName: string): Promise<{ sasUrl: string; expiresAt: number }> {
        if (!this.isInitialized) {
            await this.initialize();
        }

        try {
            return await this.azureBlobService!.getPhotoUrl(blobName);
        } catch (error) {
            console.error('[EncryptedImageStorageService] Failed to get photo URL:', error);
            throw error;
        }
    }

    /**
     * Check if service is initialized
     */
    isServiceInitialized(): boolean {
        return this.isInitialized;
    }
}
